import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { selectSchool } from '../actions';
import store from '../store';

import * as d3 from 'd3';
import Tooltip from './tooltip';
import { format } from '../modules/helpers';
import filter from '../modules/filter-data';

let tooltip;
let svg;

function makeClassrooms(data, cluster) {
  // Quick helper to update the totals when changing test data
  let activeSchool;

  // SVG dimensions
  const width = 1000;
  const height = 500;
  const minHeight = 256;
  let scaleFactor;
  let totalIcons = 0;
  // SVG padding
  const padding = {
    top: 10,
    right: 10,
    bottom: 50,
    left: 10,
  };
  // Horizontal and vertical spacing between the icons
  const numCols = 20;
  const numRows = 9;
  const xGutter = 15;
  const yGutter = 15;
  let offset = 0;

  // Top level elements
  let elm;

  let aside;
  let main;
  let key;
  let pictoGrid;
  const schools_data = filter.studentClassrooms(data.schools);
  // Final gid dimensions
  const xSize =
    (width - (padding.left + padding.right) - xGutter * (numCols - 1)) /
    numCols;
  const ySize =
    (height - (padding.top + padding.bottom) - yGutter * (numRows - 1)) /
    numRows;

  function initialiseElements() {
    elm = document.createElement('div');

    aside = d3.select(elm).append('div').attr('class', 'aside');

    main = d3.select(elm).append('div').attr('class', 'main');

    svg = main.append('svg').attr('viewBox', `0 0 ${width} ${height}`);
  }

  function setupToolTip() {
    const content = `<div class="tooltip-content-wrapper">
      <div class="tooltip-content">
      <div>
        <p class="tooltip-title"></p>
        <p class="tooltip-split"></p>
        <p class="a11y">Press enter to navigate to this school's page or tab to select another school.</p>
      </div>
      </div>
    </div>`;

    tooltip = new Tooltip(content, 'tooltip-arrow');
    main.node().appendChild(tooltip.elm);
  }

  const handleClick = function(d) {
    store.dispatch(
      selectSchool({
        classrooms: {
          selectedSchool: d,
          showTooltip: true,
        },
      })
    );
  };

  function calculateTotalClassrooms() {
    schools_data.forEach(obj => {
      if (obj.isActive) {
        activeSchool = obj;
      }
    });
  }

  function calculateScaleFactor() {
    scaleFactor = Math.ceil(
      (data.classrooms.permanent + data.classrooms.demountable) /
        (numCols * numRows)
    );

    // Determine if edgecase where rounding per group causes number of icons to be
    // greater than grid space available
    schools_data.forEach(obj => {
      totalIcons += Math.ceil(obj.classrooms.permanent / scaleFactor);
      totalIcons += Math.ceil(obj.classrooms.demountable / scaleFactor);
    });

    // If edgecase adjust scale factor
    if (totalIcons > numCols * numRows) {
      scaleFactor = Math.ceil(totalIcons * scaleFactor / (numCols * numRows));
    }
  }

  // This is a really quick and dirty helper pluralise function, it just adds 's'
  function pluralise(number, word) {
    return number <= 1 ? word : `${word}s`;
  }

  function drawAside() {
    let breakdown;
    let card;

    if (activeSchool) {
      card = aside.append('div').attr('class', 'card--highlight');

      card.append('p').attr('class', 'card-title').text(activeSchool.name);

      card
        .append('p')
        .attr('class', 'big-number')
        .text(
          format(
            activeSchool.classrooms.permanent +
              activeSchool.classrooms.demountable
          )
        );

      breakdown = card.append('p').attr('class', 'card-breakdown');

      breakdown
        .append('span')
        .html(`${format(activeSchool.classrooms.permanent)} <br>permanent`);

      breakdown.append('span').text('+');

      breakdown
        .append('span')
        .html(`${format(activeSchool.classrooms.demountable)} <br>demountable`);
    }

    card = aside.append('div').attr('class', 'card--highlight-alt');

    card.append('p').attr('class', 'card-title').text(() => {
      if (activeSchool) {
        return `${cluster} schools group*`;
      }
      return `${cluster} schools group`;
    });

    card
      .append('p')
      .attr('class', 'big-number')
      .text(format(data.classrooms.permanent + data.classrooms.demountable));

    breakdown = card.append('p').attr('class', 'card-breakdown');

    breakdown
      .append('span')
      .html(`${format(data.classrooms.permanent)} <br>permanent`);

    breakdown.append('span').text('+');

    breakdown
      .append('span')
      .html(`${format(data.classrooms.demountable)} <br>demountable`);

    if (activeSchool) {
      aside.append('p').text(`*inc. ${activeSchool.name}`);
    }
  }

  function drawPictograph() {
    svg.attr('class', 'cluster-chart');

    // define an icon store it in svg <defs> elements as a reusable component - this geometry can be generated from Inkscape, Illustrator or similar
    const defs = svg.append('defs');

    defs
      .append('symbol')
      .attr('viewBox', '0 0 20 20')
      .attr('id', 'classroom-icon')
      .append('polygon')
      .attr('points', '9,0 0,5 0,17 7,17 7,11 11,11 11,17 18,17 18,5');

    defs
      .append('symbol')
      .attr('viewBox', '0 0 20 20')
      .attr('id', 'classroom-icon-demountable')
      .append('path')
      .attr(
        'd',
        'M9,2.3l7,3.9V15h-3v-4V9h-2H7H5v2v4H2V6.2L9,2.3 M9,0L0,5v12h7v-6h4v6h7V5L9,0L9,0z'
      );

    // create group element and create an svg <use> element for each icon
    pictoGrid = svg
      .append('g')
      .attr('id', 'pictoGrid')
      .attr('transform', `translate(${padding.left} ${padding.top})`);

    const addSchools = (group, range, iconName) => {
      const schoolGroup = group.selectAll(`.${iconName}`);

      schoolGroup
        .data(range)
        .enter()
        .append('use')
        .attr('class', iconName)
        .attr('xlink:href', `#${iconName}`)
        .attr('width', xSize)
        .attr('height', ySize)
        .attr('id', d => `icon-${d}`)
        .attr('x', d => {
          const col = d % numCols; // calculates the x position (column number) using modulus
          return col * xGutter + col * xSize; // apply the buffer and return value
        })
        .attr('y', d => {
          const whole = Math.floor(d / numCols); // calculates the y position (row number)
          return whole * yGutter + whole * ySize; // apply the buffer and return the value
        });

      // This is a bit of a hack to make the whole group focusable
      schoolGroup
        .data(range)
        .enter()
        .append('rect')
        .attr('width', xSize + xGutter)
        .attr('height', ySize + yGutter)
        .attr('fill', 'transparent')
        .attr('id', d => `icon-${d}`)
        .attr('x', d => {
          const col = d % numCols; // calculates the x position (column number) using modulus
          return col * xGutter + col * xSize; // apply the buffer and return value
        })
        .attr('y', d => {
          const whole = Math.floor(d / numCols); // calculates the y position (row number)
          return whole * yGutter + whole * ySize; // apply the buffer and return the value
        });

      return schoolGroup;
    };

    const addSchoolGroup = data => {
      const permanentStart = offset;
      const permanentEnd =
        offset + Math.ceil(data.classrooms.permanent / scaleFactor);
      const demountableStart = permanentEnd;
      const demountableEnd =
        permanentEnd + Math.ceil(data.classrooms.demountable / scaleFactor);
      const permanent = d3.range(permanentStart, permanentEnd);
      const demountable = d3.range(demountableStart, demountableEnd);

      offset = demountableEnd;

      const classroomGroup = pictoGrid
        .append('g')
        .attr('tabindex', 0)
        .attr('id', `classroom-${data.uid}`)
        .attr(
          'class',
          () => (data.isActive ? 'active-group class-group' : 'class-group')
        )
        .on('click', function() {
          handleClick(data);
        })
        .on('keydown', function() {
          const e = d3.event;
          const keys = {
            SPACE: 32,
            ENTER: 13,
          };
          if (
            e.type === 'click' ||
            e.keyCode === keys.SPACE ||
            e.keyCode === keys.ENTER
          ) {
            handleClick(data);
          }
        });

      addSchools(classroomGroup, permanent, 'classroom-icon');
      addSchools(classroomGroup, demountable, 'classroom-icon-demountable');

      // No line at start!
      if (permanentStart !== 0) {
        const lineMargins = ySize * 0.1;
        let col = permanentStart % numCols;
        let row = Math.floor(permanentStart / numCols);
        // If the new group starts on a new line move the
        // line to the last col of the previous row
        if (col === 0) {
          col = numCols;
          row = row - 1;
        }

        const xPos = col * xGutter + col * xSize;
        const yPos = row * yGutter + row * ySize;

        classroomGroup
          .append('line')
          .attr('x1', xPos - 8)
          .attr('y1', yPos + lineMargins - 3)
          .attr('x2', xPos - 8)
          .attr('y2', yPos + ySize - lineMargins)
          .attr('stroke-width', 2)
          .attr('stroke', '#333');
      }
    };

    // Main data loop
    schools_data.forEach((obj, i) => {
      addSchoolGroup(obj);
    });
  }

  function drawKey() {
    key = main.append('div').attr('class', 'pictograph-key');
    const container = key.append('div');

    const key1 = container.append('div');

    key1
      .append('svg')
      .attr('class', 'classroom-icon')
      .append('use')
      .attr('xlink:href', '#classroom-icon');

    key1
      .append('div')
      .text(`${scaleFactor} permanent ${pluralise(scaleFactor, 'classroom')}`);

    const key2 = container.append('div');

    key2
      .append('svg')
      .attr('class', 'classroom-icon')
      .append('use')
      .attr('xlink:href', '#classroom-icon-demountable');

    key2
      .append('div')
      .text(
        `${scaleFactor} demountable ${pluralise(scaleFactor, 'classroom')}`
      );
  }

  function resizeViewbox() {
    const totalRows = Math.ceil(totalIcons / numCols);
    const viewboxHeight = Math.max(totalRows * (ySize + yGutter), minHeight);
    svg.attr('viewBox', `0 0 ${width} ${viewboxHeight}`);
  }

  initialiseElements();
  setupToolTip();
  calculateScaleFactor();
  resizeViewbox();
  drawKey();
  key
    .append('div')
    .style('padding', '8px 0 0 0')
    .style('width', '100%')
    .text('Data subject to change.*');
  calculateTotalClassrooms();
  drawAside();
  drawPictograph();

  elm.classList.add('chart');

  return elm;
}

class ClassroomTypes extends Component {
  wrapChart() {
    this.container = findDOMNode(this);
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    const { data } = this.props;
    const chart = makeClassrooms(data, data.name);
    this.container.appendChild(chart);
  }

  componentDidMount() {
    this.wrapChart();
  }
  componentDidUpdate() {
    if (d3.event) d3.event.stopPropagation();

    const { selectedSchool, showTooltip } = this.props.ui;
    if (showTooltip && selectedSchool) {
      svg.selectAll('.selected-group').classed('selected-group', false);
      svg
        .select(`#classroom-${selectedSchool.uid}`)
        .classed('selected-group', true);

      tooltip.onClick(function() {
        if (window.parentIFrame) {
          window.parentIFrame.sendMessage({
            type: 'change:school',
            school: selectedSchool.uid,
          });
        } else {
          window.location.search = `school=${selectedSchool.uid}`;
        }
      });

      tooltip.elm.classList.add('tooltip-arrow');
      tooltip.elm.classList.remove('tooltip-active');

      if (selectedSchool.isActive) {
        tooltip.elm.classList.remove('tooltip-arrow');
        tooltip.elm.classList.add('tooltip-active');
      }

      tooltip.show();

      // Custom Select
      tooltip.elm.querySelector('.tooltip-title').innerHTML =
        selectedSchool.name;
      tooltip.elm.querySelector('.tooltip-split').innerHTML = `${selectedSchool
        .classrooms.permanent} Perm / ${selectedSchool.classrooms
        .demountable} Demo`;

      // Custom BB
      const bb = svg
        .select(`#classroom-${selectedSchool.uid}`)
        .node()
        .getBoundingClientRect();

      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const top = bb.top + scrollY - tooltip.elm.clientHeight - 10;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;
      const left =
        bb.left + bb.width / 2 + scrollX - tooltip.elm.clientWidth / 2;
      tooltip.move(top, left);
    }
  }

  render() {
    return <div />;
  }
}

const mapStateToProps = (state, ownProps) => ({
  ui: state.ui[ownProps.chartName],
});

const mapDispatchToProps = dispatch => ({
  selectSchool: p => dispatch(selectSchool(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClassroomTypes);



// WEBPACK FOOTER //
// ./src/components/classrooms.js